import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { 
  Truck, Shield, CheckCircle, AlertTriangle, Save, RefreshCw, FileText, Baby, Download, FilePlus
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import API from '@/config/api';
import DocumentUploader from '@/components/DocumentUploader';

const TransportationTrainingForm = ({ open, onClose, staff, getAuthHeaders }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trainingItems, setTrainingItems] = useState([]);
  const [formData, setFormData] = useState({
    items: {},
    license_number: '',
    license_state: '',
    license_expiry: '',
    training_date: '',
    trainer_name: '',
    trainer_title: '',
    notes: '',
    last_annual_review: ''
  });

  useEffect(() => {
    if (open && staff) {
      fetchData();
    }
  }, [open, staff]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, dataRes] = await Promise.all([
        axios.get(`${API}/staff/transportation-training-items`, getAuthHeaders()),
        axios.get(`${API}/staff/${staff.id}/transportation-training`, getAuthHeaders())
      ]);
      setTrainingItems(itemsRes.data.items || []);
      setFormData({
        items: dataRes.data.items || {},
        license_number: dataRes.data.license_number || '',
        license_state: dataRes.data.license_state || '',
        license_expiry: dataRes.data.license_expiry || '',
        training_date: dataRes.data.training_date || '',
        trainer_name: dataRes.data.trainer_name || '',
        trainer_title: dataRes.data.trainer_title || '',
        notes: dataRes.data.notes || '',
        last_annual_review: dataRes.data.last_annual_review || ''
      });
    } catch (error) {
      console.error('Failed to load transportation training data');
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = (itemId, checked) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          completed: checked,
          date: checked ? (prev.items[itemId]?.date || new Date().toISOString().split('T')[0]) : ''
        }
      }
    }));
  };

  const handleItemDate = (itemId, date) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          date: date
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/staff/${staff.id}/transportation-training`, formData, getAuthHeaders());
      toast.success('Transportation training record saved');
      onClose();
    } catch (error) {
      toast.error('Failed to save training record');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await axios.get(`${API}/staff/${staff.id}/forms/transportation-training`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (staff?.user_name || 'driver').replace(/[^a-z0-9]/gi, '_');
      a.download = `DCF_251_08_Transportation_Training_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded — sign it and upload the signed copy below');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const requirementItems = trainingItems.filter(i => i.section === 'requirements');
  const trainingCheckItems = trainingItems.filter(i => i.section === 'training');

  const completedCount = trainingItems.filter(i => formData.items[i.id]?.completed).length;
  const totalCount = trainingItems.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="transport-training-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-teal-600" />
            Transportation Driver Training Record
          </DialogTitle>
          <DialogDescription>
            DCF 251.08 — {staff?.user_name || 'Staff Member'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6 pb-4">
              {/* Progress */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold",
                  progress === 100 ? "bg-green-100 text-green-700" : 
                  progress >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                )}>
                  {progress}%
                </div>
                <div>
                  <p className="font-medium text-sm">{completedCount} of {totalCount} items completed</p>
                  <p className="text-xs text-muted-foreground">
                    {progress === 100 ? 'All requirements and training complete' : 'Complete all items before driver transports children'}
                  </p>
                </div>
              </div>

              {/* Driver License Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-600" />
                    Driver License Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">License Number</Label>
                    <Input
                      value={formData.license_number}
                      onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                      placeholder="DL number"
                      data-testid="license-number-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">State</Label>
                    <Input
                      value={formData.license_state}
                      onChange={(e) => setFormData({...formData, license_state: e.target.value})}
                      placeholder="e.g. WI"
                      data-testid="license-state-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.license_expiry}
                      onChange={(e) => setFormData({...formData, license_expiry: e.target.value})}
                      data-testid="license-expiry-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Requirements */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Section A: Driver Requirements — DCF 251.08(4)(a)(c)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Must be completed before the driver first transports children</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {requirementItems.map((item) => (
                    <div key={item.id} className={cn(
                      "p-3 rounded-lg border transition-colors",
                      formData.items[item.id]?.completed ? "bg-green-50 border-green-200" : "bg-muted/30 border-border"
                    )}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={formData.items[item.id]?.completed || false}
                          onCheckedChange={(checked) => handleItemToggle(item.id, checked)}
                          data-testid={`req-${item.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{item.label}</p>
                            <Badge variant="outline" className="text-xs">{item.code}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          {formData.items[item.id]?.completed && (
                            <div className="mt-2">
                              <Label className="text-xs">Date Completed</Label>
                              <Input
                                type="date"
                                className="w-40 h-7 text-xs mt-0.5"
                                value={formData.items[item.id]?.date || ''}
                                onChange={(e) => handleItemDate(item.id, e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Training Items */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Section B: Transportation Training — DCF 251.08(4)(b)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Training to be provided before driver first transports children, reviewed and updated annually</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {trainingCheckItems.map((item, idx) => (
                    <div key={item.id} className={cn(
                      "p-3 rounded-lg border transition-colors",
                      formData.items[item.id]?.completed ? "bg-green-50 border-green-200" : "bg-muted/30 border-border"
                    )}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={formData.items[item.id]?.completed || false}
                          onCheckedChange={(checked) => handleItemToggle(item.id, checked)}
                          data-testid={`training-${item.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{idx + 1}. {item.label}</p>
                            <Badge variant="outline" className="text-xs">{item.code}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          {formData.items[item.id]?.completed && (
                            <div className="mt-2">
                              <Label className="text-xs">Date Completed</Label>
                              <Input
                                type="date"
                                className="w-40 h-7 text-xs mt-0.5"
                                value={formData.items[item.id]?.date || ''}
                                onChange={(e) => handleItemDate(item.id, e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Training Record */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Section C: Training Record
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Initial Training Date</Label>
                    <Input
                      type="date"
                      value={formData.training_date}
                      onChange={(e) => setFormData({...formData, training_date: e.target.value})}
                      data-testid="training-date-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Last Annual Review</Label>
                    <Input
                      type="date"
                      value={formData.last_annual_review}
                      onChange={(e) => setFormData({...formData, last_annual_review: e.target.value})}
                      data-testid="annual-review-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Trainer Name</Label>
                    <Input
                      value={formData.trainer_name}
                      onChange={(e) => setFormData({...formData, trainer_name: e.target.value})}
                      placeholder="Trainer full name"
                      data-testid="trainer-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Trainer Title</Label>
                    <Input
                      value={formData.trainer_title}
                      onChange={(e) => setFormData({...formData, trainer_title: e.target.value})}
                      placeholder="e.g. Center Director"
                      data-testid="trainer-title-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Additional notes about training..."
                      rows={3}
                      data-testid="training-notes"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* DOT Info */}
              <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                <p className="font-medium mb-1">Driving Record Information:</p>
                <p>Contact the WI Department of Transportation at (608) 261-2566 or visit 
                  <a href="https://wisconsindot.gov/Pages/dmv/vehicles/vhcl-rcd-hst/records.aspx" 
                     target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                    wisconsindot.gov
                  </a>
                </p>
              </div>

              {/* Signed Form Documents */}
              <Card className="border-teal-200 bg-teal-50/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FilePlus className="w-4 h-4 text-teal-600" />
                    Signed Training Form & Supporting Documents
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Download the form, collect signatures, then upload the signed copy here for your records.
                  </p>
                </CardHeader>
                <CardContent>
                  {staff?.id && (
                    <DocumentUploader
                      entityType="staff"
                      entityId={staff.id}
                      entityName={staff?.user_name || 'Driver'}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadPDF}
                  className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="download-transport-training-pdf"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-transport-training">
                    {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {saving ? 'Saving...' : 'Save Training Record'}
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransportationTrainingForm;
